// src/Monetization/PricingPage.js
import React, {useState, useCallback, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {getFunctions, httpsCallable} from 'firebase/functions';
import {usePlan, PLAN_LIMITS} from '../context/PlanContext';
import {useAuth} from '../Auth/AuthContext';
import './PricingPage.css';

// ── Precios (actualiza aquí si cambias precios en Stripe) ─────────────────
const PRICES = {
  monthly: {amount: '4,99\u00a0€', period: '/mes', annualNote: null},
  annual: {
    amount: '3,33\u00a0€',
    period: '/mes',
    annualNote: 'Facturado como 39,99\u00a0€/año',
    badge: '2 meses gratis',
  },
};

// ── Listas de features ────────────────────────────────────────────────────
const BASIC_FEATURES = [
  {text: 'Subir recetas (texto, pasos, ingredientes)', ok: true},
  {text: '1 foto por receta', ok: true},
  {text: `Importar receta desde texto (${PLAN_LIMITS.basic.importTextPerMonth}/mes)`, ok: true},
  {text: `Importar desde foto (${PLAN_LIMITS.basic.importPhotoPerMonth}/mes)`, ok: true},
  {text: '1 grabación de voz de prueba', ok: true},
  {text: `${PLAN_LIMITS.basic.chatPerMonth} chats con IA al mes`, ok: true},
  {text: 'Publicidad', ok: false},
  {text: 'Múltiples fotos por receta', ok: false},
  {text: 'Vídeos en recetas', ok: false},
  {text: 'Chat IA ilimitado', ok: false},
  {text: 'Grabación de voz ilimitada', ok: false},
];

const PREMIUM_FEATURES = [
  {text: 'Todo lo del plan Básico, y además:', ok: null},
  {text: 'Sin publicidad', ok: true},
  {text: 'Múltiples fotos por receta', ok: true},
  {text: 'Vídeos en recetas', ok: true},
  {text: 'Chat IA ilimitado', ok: true},
  {text: 'Grabación de voz ilimitada', ok: true},
  {text: 'Importar receta desde texto (ilimitado)', ok: true},
  {text: 'Importar desde foto (ilimitado)', ok: true},
  {text: 'Acceso prioritario a nuevas funciones', ok: true},
];

// ── Componente principal ──────────────────────────────────────────────────
const PricingPage = () => {
  const {isPremium, plan} = usePlan();
  const {userId} = useAuth();
  const navigate = useNavigate();

  const [billing, setBilling] = useState('monthly');
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState(null);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [banner, setBanner] = useState(null);

  // Detecta ?success=true o ?canceled=true tras el pago
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success')) {
      setBanner('success');
      window.history.replaceState({}, '', '/pricing');
    } else if (params.get('canceled')) {
      setBanner('canceled');
      window.history.replaceState({}, '', '/pricing');
    }
  }, []);

  // Valida el cupón contra la Cloud Function
  const validateCoupon = useCallback(async () => {
    if (!couponCode.trim()) return;
    setCheckingCoupon(true);
    try {
      const fn = httpsCallable(
        getFunctions(undefined, 'europe-west1'),
        'validateCoupon',
      );
      const result = await fn({code: couponCode.trim()});
      setCouponStatus(result.data);
    } catch {
      setCouponStatus({valid: false, message: 'Error al verificar el cupón'});
    } finally {
      setCheckingCoupon(false);
    }
  }, [couponCode]);

  // Inicia el flujo de checkout en Stripe
  const handleCheckout = useCallback(async () => {
    if (!userId) return;
    setLoadingCheckout(true);
    try {
      const fn = httpsCallable(
        getFunctions(undefined, 'europe-west1'),
        'createCheckoutSession',
      );
      const result = await fn({
        billingPeriod: billing,
        couponCode: couponStatus?.valid ? couponCode.trim() : undefined,
      });
      window.location.href = result.data.url;
    } catch (e) {
      console.error('[PricingPage] checkout', e);
      setBanner('error');
    } finally {
      setLoadingCheckout(false);
    }
  }, [userId, billing, couponCode, couponStatus]);

  // Abre el portal de Stripe para gestionar la suscripción
  const handleManageSubscription = useCallback(async () => {
    setLoadingPortal(true);
    try {
      const fn = httpsCallable(
        getFunctions(undefined, 'europe-west1'),
        'createPortalSession',
      );
      const result = await fn();
      window.location.href = result.data.url;
    } catch (e) {
      console.error('[PricingPage] portal', e);
    } finally {
      setLoadingPortal(false);
    }
  }, []);

  return (
    <div className="pricing-page">

      {/* ── Banner de estado ─────────────────────────────────────────── */}
      {banner === 'success' && (
        <div className="pricing-banner pricing-banner--success">
          🎉 ¡Bienvenido a Premium! Tu suscripción ya está activa.
        </div>
      )}
      {banner === 'canceled' && (
        <div className="pricing-banner pricing-banner--info">
          El proceso de pago fue cancelado. Puedes retomarlo cuando quieras.
        </div>
      )}
      {banner === 'error' && (
        <div className="pricing-banner pricing-banner--error">
          Algo salió mal al iniciar el pago. Inténtalo de nuevo.
        </div>
      )}

      {/* ── Cabecera ─────────────────────────────────────────────────── */}
      <div className="pricing-header">
        <button className="pricing-back" onClick={() => navigate(-1)}>
          ← Volver
        </button>
        <h1 className="pricing-title">Elige tu plan</h1>
        <p className="pricing-subtitle">
          Cocina más, preocúpate menos
        </p>

        {/* Toggle mensual / anual */}
        <div className="pricing-toggle" role="group">
          <button
            className={`pricing-toggle-btn${billing === 'monthly' ? ' active' : ''}`}
            onClick={() => setBilling('monthly')}
          >
            Mensual
          </button>
          <button
            className={`pricing-toggle-btn${billing === 'annual' ? ' active' : ''}`}
            onClick={() => setBilling('annual')}
          >
            Anual
            <span className="pricing-toggle-save">−33%</span>
          </button>
        </div>
      </div>

      {/* ── Tarjetas de planes ───────────────────────────────────────── */}
      <div className="pricing-cards">

        {/* BÁSICO */}
        <div className={`pricing-card${plan === 'basic' && !isPremium ? ' pricing-card--active' : ''}`}>
          {plan === 'basic' && !isPremium && (
            <div className="pricing-current-badge">Tu plan actual</div>
          )}
          <div className="pricing-card-header">
            <h2 className="pricing-plan-name">Básico</h2>
            <div className="pricing-price">
              <span className="pricing-price-amount">Gratis</span>
              <span className="pricing-price-period">para siempre</span>
            </div>
          </div>

          <ul className="pricing-features">
            {BASIC_FEATURES.map((f, i) => (
              <li
                key={i}
                className={`pricing-feature${f.ok === false ? ' pricing-feature--no' : ''}`}
              >
                <span className="pricing-feature-icon">
                  {f.ok === false ? '✕' : '✓'}
                </span>
                {f.text}
              </li>
            ))}
          </ul>

          <div className="pricing-card-footer">
            {!isPremium ? (
              <button className="pricing-btn pricing-btn--disabled" disabled>
                Plan actual
              </button>
            ) : (
              <button
                className="pricing-btn pricing-btn--secondary"
                onClick={handleManageSubscription}
                disabled={loadingPortal}
              >
                {loadingPortal ? 'Cargando…' : 'Cancelar suscripción'}
              </button>
            )}
          </div>
        </div>

        {/* PREMIUM */}
        <div className={`pricing-card pricing-card--premium${isPremium ? ' pricing-card--active' : ''}`}>
          <div className="pricing-premium-badge">⭐ Premium</div>
          {isPremium && (
            <div className="pricing-current-badge pricing-current-badge--premium">
              Tu plan actual
            </div>
          )}

          <div className="pricing-card-header">
            <h2 className="pricing-plan-name pricing-plan-name--premium">
              Premium
            </h2>
            <div className="pricing-price">
              <span className="pricing-price-amount pricing-price-amount--premium">
                {PRICES[billing].amount}
              </span>
              <span className="pricing-price-period">
                {PRICES[billing].period}
              </span>
            </div>
            {billing === 'annual' && (
              <div className="pricing-annual-note">
                {PRICES.annual.annualNote}
                {' · '}
                <strong>{PRICES.annual.badge}</strong>
              </div>
            )}
            {couponStatus?.valid && (
              <div className="pricing-discount-applied">
                ✓ −{couponStatus.discountPercent}% aplicado
              </div>
            )}
          </div>

          <ul className="pricing-features">
            {PREMIUM_FEATURES.map((f, i) => (
              <li
                key={i}
                className={`pricing-feature${f.ok === null ? ' pricing-feature--header' : ''}`}
              >
                {f.ok !== null && (
                  <span className="pricing-feature-icon pricing-feature-icon--premium">✓</span>
                )}
                {f.text}
              </li>
            ))}
          </ul>

          {/* Cupón de descuento */}
          {!isPremium && (
            <div className="pricing-coupon">
              <p className="pricing-coupon-label">¿Tienes un código de descuento?</p>
              <div className="pricing-coupon-row">
                <input
                  type="text"
                  placeholder="CÓDIGO"
                  value={couponCode}
                  maxLength={32}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase());
                    setCouponStatus(null);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && validateCoupon()}
                  className={`pricing-coupon-input${
                    couponStatus?.valid === true ? ' valid'
                      : couponStatus?.valid === false ? ' invalid' : ''
                  }`}
                />
                <button
                  className="pricing-coupon-btn"
                  onClick={validateCoupon}
                  disabled={checkingCoupon || !couponCode.trim()}
                >
                  {checkingCoupon ? '…' : 'Aplicar'}
                </button>
              </div>
              {couponStatus && !couponStatus.valid && (
                <p className="pricing-coupon-feedback pricing-coupon-feedback--error">
                  {couponStatus.message}
                </p>
              )}
              {couponStatus?.valid && (
                <p className="pricing-coupon-feedback pricing-coupon-feedback--ok">
                  ✓ {couponStatus.description}
                </p>
              )}
            </div>
          )}

          <div className="pricing-card-footer">
            {isPremium ? (
              <>
                <button className="pricing-btn pricing-btn--disabled" disabled>
                  Plan actual
                </button>
                <button
                  className="pricing-btn pricing-btn--secondary"
                  onClick={handleManageSubscription}
                  disabled={loadingPortal}
                >
                  {loadingPortal ? 'Cargando…' : 'Gestionar suscripción'}
                </button>
              </>
            ) : (
              <button
                className="pricing-btn pricing-btn--premium"
                onClick={handleCheckout}
                disabled={loadingCheckout}
              >
                {loadingCheckout ? 'Redirigiendo…' : 'Suscribirme ahora →'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Pie de garantías ─────────────────────────────────────────── */}
      <p className="pricing-guarantee">
        🔒 Pago seguro con Stripe · Cancela cuando quieras · Sin permanencia
      </p>
    </div>
  );
};

export default PricingPage;
